import * as React from 'react';
import { DateTimeProps } from '../date-time/date-time-props';
import { compose } from 'redux';
import styles from './display-date.scss';
import { Label, NoValueDash } from 'ui/common';
import { DateFormat, DayFormat, MonthFormat, WeekdayFormat, YearFormat } from 'ui/common/types';

export interface DisplayDateProps extends DateTimeProps {
  /**
   * Predefined possible styles by the design handbook
   */
  style?: 'primary' | 'secondary';
  /**
   * When true, the label font is normal and the value font is bold
   */
  strong?: boolean;  
  /**
   * When true, a predefined style class to render the label above the value is applied
   */
  showStacked: boolean;
  /**
   * Predefined intl format option for weekday. 'long'
   */
  weekdayFormat?: WeekdayFormat;
  /**
   * Predefined intl format option for day. 'numeric'
   */
  dayFormat?: DayFormat;
  /**
   * Predefined intl format option for month. Default is 'long'
   */    
  monthFormat?: MonthFormat;
  /**
   * Predefined intl format option for year. Default is 'numeric'
   */
  yearFormat?: YearFormat;
  /**
   * When true, the component gets hidden when value is empty
   */
  hideIfNoValue?: boolean;

  /**
   * @internal
   */
  children?: any;
}

/**
 * Displaying a databound text value consist of rendering a label, and the value.
 * 
 * This component is responsible for the composition of the two.
 * 
 * @param props 
 */
const DisplayDateComponent = (props: Partial<DisplayDateProps>) => {  
  const {
    className,
    componentId,    
    hideIfNoValue = false,
    weekdayFormat = `${DateFormat.Long}`,
    dayFormat = `${DateFormat.Numeric}`,
    monthFormat = `${DateFormat.Long}`,
    yearFormat = `${DateFormat.Numeric}`,
    label,
    value,
    useTime    
  } = props;
    
  const isDate = value && value.getUTCHours;
  const hideComponent = hideIfNoValue && !isDate;

  if (hideComponent) {
    return null;
  }

  const dateIntlOptions = { weekday: weekdayFormat, 
                            day: dayFormat ,
                            month: monthFormat,
                            year: yearFormat
                          };
  
  const formattedDate = props.formatDate(value, dateIntlOptions);
  const formattedTime = props.useTime && props.formatTime(value);    
  const formattedValue = !!useTime ? `${formattedDate} ${formattedTime}` : `${formattedDate}`; 

  return (    
    <div className={`${className}`} id={componentId}>
      <Label label={label} />
      {formattedValue ? 
        <span className={'value-date'}>
          {formattedValue}
        </span>
        : <NoValueDash/>
      }
    </div>
);
};

/**
 * A utility that maps known props to a known style and appends them to a list.
 * 
 * The list of final style classes are concatenated into a valid JSX className string attribute
 * 
 * @param props 
 */
const styleFromProps = (props: DisplayDateProps) =>
  [
    styles.date,
    props.className,
    styles[props.style] && styles[props.style],    
    props.showStacked && styles.stackedColumn,    
    props.strong && styles.strong    
  ].filter(s => s).join(' ');

/**
 * Higher order component to override the final className
 * @param Component the component to decorate
 */
const styleVariations = (Component: any) => ({children, ...childProps}: DisplayDateProps) => (
  <Component {...childProps} className={styleFromProps(childProps)}>
    {children}
  </Component>
);

export const DisplayDate = compose(styleVariations)(DisplayDateComponent);
